import { useMemo } from 'react'
import { DateTime } from 'luxon'
import { useNavigate } from 'react-router-dom'
import { useTw } from '../../../../../../../utils/Styles'
import { getHighestStatus } from '../../../../../../../utils'
import { StatusPageQuery } from '../../../StatusPage.generated'
import { StatusPageEventHistoryItemComponentStatus as Status } from '../../../../../../../api/types'

type Props = {
  day?: string
  routeComponentId?: string
  currentComponentId: string
  history: StatusPageQuery['Profile']['statusPage']['components'][0]['eventsHistory']
}

export function StatusHistory({
  day,
  history = [],
  routeComponentId,
  currentComponentId,
}: Props) {
  const tw = useTw()
  return (
    <div
      className={tw`flex mx-auto flex-row-reverse py-2 space-x-1 space-x-reverse justify-evenly`}
    >
      {history.map((_) => (
        <StatusHistoryItem
          item={_}
          day={day}
          key={_.date}
          routeComponentId={routeComponentId}
          currentComponentId={currentComponentId}
        />
      ))}
    </div>
  )
}

type StatusHistoryItemProps = {
  day?: string
  routeComponentId?: string
  currentComponentId: string
  item: StatusPageQuery['Profile']['statusPage']['components'][0]['eventsHistory'][0]
}

export function StatusHistoryItem({
  day,
  item,
  routeComponentId,
  currentComponentId,
}: StatusHistoryItemProps) {
  const tw = useTw()
  const navigate = useNavigate()
  const urlDate = DateTime.fromISO(item.date).toFormat('yyyy-LL-dd')
  const selected = routeComponentId === currentComponentId && day === urlDate

  const timeline = useMemo(
    () =>
      item.events.flatMap((event) =>
        event.historyItems.filter((_) =>
          _.affectedComponents.some((_) => _.componentId === currentComponentId)
        )
      ),
    [item.events, currentComponentId]
  )

  const highestStatus = useMemo(
    () =>
      getHighestStatus(
        timeline.flatMap((timelineEntry) =>
          timelineEntry.affectedComponents
            .filter((component) => component.componentId === currentComponentId)
            .map((_) => _.componentStatus)
        )
      ),
    [timeline, currentComponentId]
  )

  return (
    <div
      onClick={() =>
        timeline.length &&
        navigate(`/status-page/${currentComponentId}/${urlDate}`)
      }
      className={tw`
        w-[0.375rem] h-8 rounded flex-shrink-0
        ${selected && 'ring-2 ring-offset-1'}
        ${timeline.length && 'cursor-pointer'}
        ${highestStatus === Status.MajorOutage && 'bg-red-400 ring-red-500'}
        ${highestStatus === Status.PartialOutage && 'bg-red-300 ring-red-400'}
        ${highestStatus === Status.Degraded && 'bg-yellow-300 ring-yellow-400'}
        ${highestStatus === Status.Operational && 'bg-green-400 ring-green-500'}
      `}
    />
  )
}
